"use client";

export type ManageOptionId = "checkout" | "expert";

interface ManageOption {
  id: ManageOptionId;
  title: string;
  image: string;
  /** Bold one-liner naming who the option suits. */
  headline: string;
  description: string;
  points: string[];
}

// NOTE: no assets for these two yet — placeholders at the export's sizes
// (178x160 and 213x160). Drop real files into public/get-started/ and swap.
const OPTIONS: ManageOption[] = [
  {
    id: "checkout",
    title: "Proceed to checkout",
    image: "/get-started/checkout.svg",
    headline: "Best if you’re clear on what you need.",
    description: "Select your services and complete your setup online in minutes.",
    points: [
      "Simple, guided process",
      "Transparent pricing and inclusions",
      "Available anytime",
    ],
  },
  {
    id: "expert",
    title: "Speak to an Expert",
    image: "/get-started/expert.svg",
    headline: "Best if you’d like help deciding.",
    description: "Talk to an advisor and choose the right setup for your business.",
    points: [
      "No obligation consultation",
      "Personalised recommendations",
      "Clear answers to your questions",
    ],
  },
];

/**
 * The export draws this as a 16x11 stroke at (4,6) in a 24x24 box — a tick in
 * the body-text grey rather than the blue badge used on the dark screens.
 */
function PointCheck() {
  return (
    <svg viewBox="0 0 24 24" className="mt-[2px] h-5 w-5 shrink-0 lg:h-6 lg:w-6" aria-hidden="true">
      <path
        d="M4.5 12.5l4.5 4.5L19.5 6.5"
        fill="none"
        stroke="#595D71"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HelpOptionCard({
  option,
  onSelect,
}: {
  option: ManageOption;
  onSelect: (id: ManageOptionId) => void;
}) {
  return (
    <div className="relative flex w-full max-w-[417px] pt-6 lg:pt-[42px]">
      {/* Same ambient ellipse as the service cards on the first screen: the
          design's 533px backdrop-blur is a no-op over a flat gradient, so a
          filter blur is what actually renders the glow. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-10 left-1/4 right-1/4 rounded-full bg-white/50 blur-3xl"
      />

      {/* Everything inside is a span: only phrasing content is valid in a
          button, and the whole card is the click target. */}
      <button
        type="button"
        onClick={() => onSelect(option.id)}
        className="relative flex w-full flex-col items-start gap-6 rounded-[28px] bg-white/70 px-5 py-7 text-left outline outline-2 -outline-offset-2 outline-white/30 backdrop-blur-xl transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-[#0061FE] lg:gap-[30px] lg:rounded-[34px] lg:px-[25px] lg:py-[33px]"
      >
        <span className="font-suisse-intl-bold text-2xl capitalize leading-tight text-black sm:text-[28px] lg:text-[32px]">
          {option.title}
        </span>

        <img src={option.image} alt="" className="h-[140px] w-auto lg:h-[160px]" />

        <span className="flex w-full flex-col items-start gap-2">
          {/* Sentence case, not the export's `capitalize`: that renders these
              full sentences in Title Case, which reads as a bug. Kept on the
              short card title above, matching the first screen's cards. */}
          <span className="font-suisse-intl-bold text-lg leading-snug text-black lg:text-xl">
            {option.headline}
          </span>
          <span className="font-suisse-intl text-sm leading-[22.4px] text-black lg:text-base">
            {option.description}
          </span>
        </span>

        <span className="flex w-full flex-col items-start gap-2">
          {option.points.map((point) => (
            <span key={point} className="flex w-full items-start gap-3">
              <PointCheck />
              <span className="flex-1 font-suisse-intl-medium text-sm leading-[19.6px] text-[#595D71]">
                {point}
              </span>
            </span>
          ))}
        </span>
      </button>
    </div>
  );
}

export function ManageHelpStep({ onSelect }: { onSelect: (id: ManageOptionId) => void }) {
  return (
    <div className="mx-auto flex w-full max-w-[900px] flex-col items-center gap-8 px-4 pb-16 pt-8 sm:gap-[54px] sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[62px]">
      <h1 className="max-w-[595px] text-center font-suisse-intl-bold text-[28px] leading-tight text-white sm:text-5xl lg:text-[64px] lg:leading-[71px]">
        How can we help you today?
      </h1>

      <div className="flex w-full flex-col items-center gap-2 lg:flex-row lg:items-stretch lg:justify-center lg:gap-8">
        {OPTIONS.map((option) => (
          <HelpOptionCard key={option.id} option={option} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}
